// src/components/Paper/Paper.jsx

import React, {useState, useEffect, useRef, useMemo} from 'react';
import {useNavigate, useSearchParams} from 'react-router-dom';
import Keyboard from "react-simple-keyboard";
import "react-simple-keyboard/build/css/index.css";
import './Paper.css';

import DecimalGrid from '../DecimalGrid/DecimalGrid';
import MatchingGame from '../MatchingGame/MatchingGame';
import VerticalCalc from '../VerticalCalc/VerticalCalc';
import DiyImageShow from "../DiyImageShow/DiyImageShow";

import {fetchMathApiWithAuth} from "../../utils/mathServerHttp";

// 定义题型常量
const QUESTION_TYPES = {
    CHOICE: 1,          // 选择题
    FILL_IN: 2,         // 填空题
    DECIMAL_GRID: 3,    // 小数格
    MATCHING: 4,        // 连线题
    VERTICAL_CALC: 5    // 竖式计算题
};

const HEARTS_MAX = '∞';
const HEARTS_IS_INFINITE = HEARTS_MAX ==='∞'; // 新增标记，用于判断是否为无限红心模式

const Paper = () => {
    const [searchParams] = useSearchParams();
    const chapterId = searchParams.get('chapterId');
    const chapterLevel = searchParams.get('chapterLevel');

    const paperId = searchParams.get('paperId');
    const questionIds = searchParams.get('questionIds'); // 指定问题ids

    const navigate = useNavigate();

    // 整份试卷信息
    const [paperData, setPaperData] = useState(null);
    // 题目队列（当前还没完全答对的题）
    const [questionsQueue, setQuestionsQueue] = useState([]);

    // 加载 / 错误
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // 选择题选项
    const [selectedOption, setSelectedOption] = useState(null);

    // 填空题 / 竖式题 的答案
    const [fillAnswers, setFillAnswers] = useState({});

    // 当前光标所在的空序号
    const [currentBlankOrder, setCurrentBlankOrder] = useState(1);
    // 当前键盘索引(若题目有多个键盘配置)
    const [currentKeyboardIndex, setCurrentKeyboardIndex] = useState(0);

    // type=3 答案（小数格）
    const [decimalFraction, setDecimalFraction] = useState('0.00');

    // 当前题型 => 用于弹窗逻辑
    const [currentQuestionType, setCurrentQuestionType] = useState(null);

    // 当前题中已回答正确的空数量
    const [currentCorrectBlankCount, setCurrentCorrectBlankCount] = useState(0);

    // 用于在队列空时依然展示最后一道题
    const [lastQuestion, setLastQuestion] = useState(null);

    // 进度 & 正确率
    const [answeredSubmitCount, setAnsweredSubmitCount] = useState(0); // 已提交小题
    const [answeredQuestionsCount, setAnsweredQuestionsCount] = useState(0); // 已整题答完
    const [totalQuestions, setTotalQuestions] = useState(0); // 总题目数
    const [correctCount, setCorrectCount] = useState(0); // 已答对的小题数

    // 红心
    const [heartsRemaining, setHeartsRemaining] = useState(HEARTS_MAX);

    // 检查按钮点击
    const [isChecking, setIsChecking] = useState(false);

    // 返回确认弹窗
    const [showExitConfirm, setShowExitConfirm] = useState(false);

    // 答错弹窗
    const [wrongAnswerFeedback, setWrongAnswerFeedback] = useState({
        show: false,
        correctAnswer: '',
        answerExplanation: ''
    });

    // 全部答完
    const [showResultModal, setShowResultModal] = useState(false);

    // 是否显示键盘
    const [showKeyboard, setShowKeyboard] = useState(false);
    // 键盘当前输入值（用于大多数题型的多字符场景）
    const [keyboardInput, setKeyboardInput] = useState('');

    // 防抖计时器引用（连线题用）
    const allMatchedTimer = useRef(null);
    const previousQuestionIdRef = useRef(null);
    const keyboardRef = useRef(null);

    const fetchPaperData = async () => {
        try {
            setLoading(true);
            const data = (paperId && questionIds)
                ? await fetchMathApiWithAuth(`/api/paper/questions?paperId=${paperId}&questionIds=${questionIds}`)
                : await fetchMathApiWithAuth(`/api/paper/one?chapterId=${chapterId}&chapterLevel=${chapterLevel}`);

            setPaperData(data);

            const {questions = []} = data;
            setQuestionsQueue(questions);
            setTotalQuestions(questions.length);

            // 重置
            setSelectedOption(null);
            setFillAnswers({});
            setCurrentBlankOrder(1);
            setCurrentKeyboardIndex(0);
            setAnsweredQuestionsCount(0);
            setCurrentCorrectBlankCount(0);
            setAnsweredSubmitCount(0);
            setCorrectCount(0);
            setHeartsRemaining(HEARTS_MAX);
            setWrongAnswerFeedback({
                show: false,
                correctAnswer: '',
                answerExplanation: ''
            });
            setShowResultModal(false);
            setLastQuestion(null);
            setCurrentQuestionType(null);
            setShowKeyboard(false);
            clearKeyboardInput();
            setDecimalFraction('0.00');
            previousQuestionIdRef.current = null;
        } catch (err) {
            console.log(err);
            setError("暂无试卷数据");
        } finally {
            setLoading(false);
        }
    };

    // 加载试卷
    useEffect(() => {
        fetchPaperData();
    }, [chapterId, chapterLevel]);

    useEffect(() => {
        const handleEsc = (e) => {
            if (e.key === 'Escape' && showExitConfirm) {
                setShowExitConfirm(false);
            }
        };
        window.addEventListener('keydown', handleEsc);
        return () => window.removeEventListener('keydown', handleEsc);
    }, [showExitConfirm]);

    const handleGoBack = () => {
        // 新增确认弹窗
        setShowExitConfirm(true);
    };

// 新增：确认退出处理
    const handleExitConfirm = () => {
        navigate('/');
        setShowExitConfirm(false);
    };

// 新增：取消退出
    const handleExitCancel = () => {
        setShowExitConfirm(false);
    };

    const handleOptionClick = (optionName) => {
        setSelectedOption(optionName);
    };

    // 封装提交答案的逻辑
    const submitBlankAnswer = async (currentQuestion, blankOrder) => {
        if (isChecking) return;
        setIsChecking(true);
        try {
            const payload = {
                paperId: paperData.paperId,
                paperCopyId: paperData.paperCopyId,
                questionId: currentQuestion.questionId,
                answer: fillAnswers[blankOrder],
                blankOrder: blankOrder,
                finished: questionsQueue.length === 1 && currentCorrectBlankCount === currentQuestion.blankCount - 1
            };

            const data = await fetchMathApiWithAuth('/api/question/answer', {
                method: 'POST',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify(payload)
            });

            setAnsweredSubmitCount(prev => prev + 1);

            if (data.correct) {
                // 小题答对
                setCorrectCount(prev => prev + 1);
                setCurrentCorrectBlankCount(prev => prev + 1);
                return true;
            } else {
                // 答错
                if (!HEARTS_IS_INFINITE) {
                    setHeartsRemaining(prev => Math.max(0, prev - 1));
                }
                setCurrentQuestionType(currentQuestion.type);

                if (HEARTS_IS_INFINITE || heartsRemaining > 1) {
                    setWrongAnswerFeedback({
                        show: true,
                        correctAnswer: data.correctAnswer || '',
                        answerExplanation: data.answerExplanation || ''
                    });
                }
                return false;
            }
        } catch (err) {
            console.error(err);
            setError('提交答案时发生错误，请稍后重试。');
            return false;
        } finally {
            setIsChecking(false);
        }
    };

    /**
     * 重要修改点：
     * 点击填空 / 竖式空时，根据空号计算 keyboardIndex，
     * 并把 keyboardInput 清空（不带入旧空的值）。
     */
    const handleFillClick = async (blankOrder) => {
        if (!questionToDisplay) return;
        // 检查该空是否已经回答过，如果已回答则不处理
        if (fillAnswers[blankOrder]) return;

        const currentQuestion = questionsQueue[0];
        // 检查该空是否已经回答过，如果已回答则尝试提交答案
        if (fillAnswers[currentBlankOrder] && [QUESTION_TYPES.FILL_IN, QUESTION_TYPES.VERTICAL_CALC].includes(currentQuestion.type)) {
            const isCorrect = await submitBlankAnswer(currentQuestion, currentBlankOrder);
            if (!isCorrect) return; // 答错不切换空
        }

        // 根据空号计算 keyboardIndex
        let kbIdx = blankOrder - 1;
        if (kbIdx >= questionToDisplay.keyboards?.length) {
            kbIdx = questionToDisplay.keyboards?.length - 1;
        }
        if (kbIdx < 0) kbIdx = 0;

        setCurrentBlankOrder(blankOrder);
        setCurrentKeyboardIndex(kbIdx);

        // 清空键盘输入
        clearKeyboardInput();
        setShowKeyboard(true);
    };

    const handleKeyboardChange = (input) => {
        if (!questionToDisplay) return;
        // 竖式题：在 handleKeyPress 里单字符处理，这里可直接 return
        if (questionToDisplay.type === QUESTION_TYPES.VERTICAL_CALC) {
            return;
        }
        // 普通填空题：跟随输入
        setKeyboardInput(input);
        setFillAnswers((prev) => ({
            ...prev,
            [currentBlankOrder]: input
        }));
    };

    const handleKeyPress = (button) => {
        if (!questionToDisplay) return;

        if (button === '{bksp}') {
            // 对竖式题，点退格直接清空当前空
            if (questionToDisplay.type === QUESTION_TYPES.VERTICAL_CALC) {
                setFillAnswers((prev) => ({
                    ...prev,
                    [currentBlankOrder]: ''
                }));
            }
            return;
        }

        if (button === '{done}') {
            // 不先隐藏键盘，避免闪动，直接提交答案
            handleSubmitAnswer();
            return;
        }

        // 竖式题单字符处理
        if (questionToDisplay.type === QUESTION_TYPES.VERTICAL_CALC) {
            setFillAnswers((prev) => ({
                ...prev,
                [currentBlankOrder]: button
            }));
        }
    };

    // 根据 keyboards 配置，生成 layout
    const getKeyboardLayout = (kbItem) => {
        if (!kbItem) return null;
        switch (kbItem.type) {
            case 'none':
                return null;
            case 'numeric':
                return [
                    '1 2 3 4',
                    '5 6 7 8',
                    '9 0',
                    '{done} {bksp}'
                ];
            case 'point_numeric':
                return [
                    '1 2 3 4',
                    '5 6 7 8',
                    '9 0 .',
                    '{done} {bksp}'
                ];
            case 'semi_numeric':
                return [
                    '1 2 3 4',
                    '5 6 7 8',
                    '9 0 /',
                    '{done} {bksp}'
                ];
            case 'sign_numeric':
                return [
                    '1 2 3 4',
                    '5 6 7 8',
                    '9 0 + -',
                    '{done} {bksp}'
                ];
            case 'compare':
                return [
                    '< > =',
                    '{done} {bksp}'
                ];
            case 'diy':
                if (!Array.isArray(kbItem.buttons)) return null;
                const diyRows = kbItem.buttons.map(row => row.join(' '));
                diyRows.push('{done} {bksp}');
                return diyRows;
            default:
                return null;
        }
    };

    const isOperators = (str) => /^[+\-×÷./]$/.test(str);

    // 自动给非特殊键加上 hg-button-normal
    const buildButtonThemeFromLayout = (layoutRows = []) => {
        const normalKeys = [];
        const operatorKeys = [];
        const doneKeys = [];
        const backKeys = [];

        layoutRows.forEach(row => {
            const items = row.split(' ');
            items.forEach(btn => {
                if (btn === '{done}') {
                    doneKeys.push('{done}');
                } else if (btn === '{bksp}') {
                    backKeys.push('{bksp}');
                } else if (isOperators(btn)) {
                    operatorKeys.push(btn);
                } else {
                    normalKeys.push(btn);
                }
            });
        });

        const theme = [];
        if (normalKeys.length > 0) {
            theme.push({
                class: "hg-button-normal",
                buttons: normalKeys.join(' ')
            });
        }
        if (operatorKeys.length > 0) {
            theme.push({
                class: "hg-button-operators",
                buttons: operatorKeys.join(' ')
            });
        }
        if (backKeys.length > 0) {
            theme.push({
                class: "hg-button-back",
                buttons: backKeys.join(' ')
            });
        }
        if (doneKeys.length > 0) {
            theme.push({
                class: "hg-button-done",
                buttons: doneKeys.join(' ')
            });
        }
        return theme;
    };

    // 跳到下一题(若整题答错则放回队尾)
    const goToNextQuestion = (isCorrect) => {
        setQuestionsQueue((prevQueue) => {
            const newQueue = [...prevQueue];
            const [currentQ] = newQueue.splice(0, 1);
            setLastQuestion(currentQ);
            if (!isCorrect) {
                // 答错放队尾
                newQueue.push(currentQ);
            }
            return newQueue;
        });

        // 重置
        setSelectedOption(null);
        setFillAnswers({});
        setCurrentBlankOrder(1);
        setCurrentKeyboardIndex(0);
        setCurrentQuestionType(null);
        setShowKeyboard(false);
        setCurrentCorrectBlankCount(0);
        clearKeyboardInput();
        setDecimalFraction('0.00');
        previousQuestionIdRef.current = null;
    };

    // 答错弹窗 => 我知道了
    const handleConfirmFeedback = () => {
        setWrongAnswerFeedback({
            show: false,
            correctAnswer: '',
            answerExplanation: ''
        });

        const currentQuestion = questionsQueue[0];

        // 选择题 / 小数格 / 填空题或竖式计算题只有一个空时 => 直接下一题
        if (currentQuestionType === QUESTION_TYPES.CHOICE || currentQuestionType === QUESTION_TYPES.DECIMAL_GRID ||
            ((currentQuestionType === QUESTION_TYPES.FILL_IN || currentQuestionType === QUESTION_TYPES.VERTICAL_CALC) && currentQuestion.blankCount === 1)) {
            goToNextQuestion(false);
        }
        // 填空题 / 竖式计算题有多个空 => 不自动跳题
        else if (currentQuestionType === QUESTION_TYPES.FILL_IN || currentQuestionType === QUESTION_TYPES.VERTICAL_CALC) {
            setFillAnswers(prev => ({
                ...prev,
                [currentBlankOrder]: ''
            }));
            clearKeyboardInput();
            setShowKeyboard(true);
        }
        // 连线题 => 不跳题
    };

    // 提交当前空答案(仅针对 选择题、填空题、小数格、竖式题)
    const handleSubmitAnswer = async () => {
        if (isChecking) return; // 如果正在检查，直接返回
        if (!paperData || questionsQueue.length === 0) return;
        const currentQuestion = questionsQueue[0];
        if (!currentQuestion) return;

        if ([QUESTION_TYPES.FILL_IN, QUESTION_TYPES.VERTICAL_CALC].includes(currentQuestion.type)) {
            if (!fillAnswers[currentBlankOrder]) return;
            const isCorrect = await submitBlankAnswer(currentQuestion, currentBlankOrder);
            if (isCorrect) {
                // 对于填空题和竖式题，处理后续逻辑
                if ([QUESTION_TYPES.FILL_IN, QUESTION_TYPES.VERTICAL_CALC].includes(currentQuestion.type)) {
                    // 查找下一个未填写的空
                    let nextBlankOrder = null;
                    for (let i = 1; i <= currentQuestion.blankCount; i++) {
                        if (!fillAnswers[i]) {
                            nextBlankOrder = i;
                            break;
                        }
                    }

                    if (nextBlankOrder) {
                        // 有未填写的空，定位到该空
                        let kbIdx = nextBlankOrder - 1;
                        if (kbIdx >= currentQuestion.keyboards?.length) {
                            kbIdx = currentQuestion.keyboards?.length - 1;
                        }
                        if (kbIdx < 0) kbIdx = 0;

                        setCurrentBlankOrder(nextBlankOrder);
                        setCurrentKeyboardIndex(kbIdx);
                        clearKeyboardInput();
                        setShowKeyboard(true);
                    } else {
                        // 所有空都已填写 => 整题完成
                        setAnsweredQuestionsCount(prev => prev + 1);
                        goToNextQuestion(true);
                    }
                }
            }
        } else {
            // 处理选择题、小数格等其他类型题目的逻辑
            try {
                setIsChecking(true);
                const payload = {
                    paperId: paperData.paperId,
                    paperCopyId: paperData.paperCopyId,
                    questionId: currentQuestion.questionId,
                    finished: questionsQueue.length === 1 && currentCorrectBlankCount === currentQuestion.blankCount - 1
                };

                if (currentQuestion.type === QUESTION_TYPES.CHOICE) {
                    // 选择题
                    if (!selectedOption) return;
                    payload.answer = selectedOption;
                    payload.blankOrder = 1;
                } else if (currentQuestion.type === QUESTION_TYPES.DECIMAL_GRID) {
                    // 小数格
                    if (!decimalFraction || decimalFraction === '0.00' || decimalFraction === '0.0') {
                        // alert('请涂色小数格');
                        return;
                    }
                    payload.answer = decimalFraction;
                    payload.blankOrder = 1;
                }

                const data = await fetchMathApiWithAuth('/api/question/answer', {
                    method: 'POST',
                    headers: {'Content-Type': 'application/json'},
                    body: JSON.stringify(payload)
                });

                setAnsweredSubmitCount(prev => prev + 1);

                if (data.correct) {
                    // 小题答对
                    setCorrectCount(prev => prev + 1);
                    setCurrentCorrectBlankCount(prev => prev + 1);

                    // 对于 (选择题 / 小数题) => 整题完成
                    if (currentQuestion.type === QUESTION_TYPES.CHOICE || currentQuestion.type === QUESTION_TYPES.DECIMAL_GRID) {
                        setAnsweredQuestionsCount(prev => prev + 1);
                        goToNextQuestion(true);
                    }
                } else {
                    // 答错
                    if (!HEARTS_IS_INFINITE) {
                        setHeartsRemaining(prev => Math.max(0, prev - 1));
                    }
                    setCurrentQuestionType(currentQuestion.type);

                    if (HEARTS_IS_INFINITE || heartsRemaining > 1) {
                        setWrongAnswerFeedback({
                            show: true,
                            correctAnswer: data.correctAnswer || '',
                            answerExplanation: data.answerExplanation || ''
                        });
                    }

                    // 对于选择题 / 小数题 => 本题结束
                    if (currentQuestion.type === QUESTION_TYPES.CHOICE || currentQuestion.type === QUESTION_TYPES.DECIMAL_GRID) {
                        setAnsweredQuestionsCount(prev => prev + 1);
                    }
                }
            } catch (err) {
                console.error(err);
                setError('提交答案时发生错误，请稍后重试。');
            } finally {
                setIsChecking(false);
            }
        }
    };

    // 提交「连线题」的答案
    const handleLinkLinkPair = async (leftIndex, rightIndex, onServerChecked) => {
        if (!paperData || questionsQueue.length === 0) return;
        const currentQuestion = questionsQueue[0];
        if (!currentQuestion || currentQuestion.type !== QUESTION_TYPES.MATCHING) return;

        try {
            const payload = {
                paperId: paperData.paperId,
                paperCopyId: paperData.paperCopyId,
                questionId: currentQuestion.questionId,
                blankOrder: leftIndex + 1, // 每连一次线，约定 blankOrder = leftIndex+1
                answer: (rightIndex + 1).toString(),
                finished: questionsQueue.length === 1 && currentCorrectBlankCount === currentQuestion.blankCount - 1
            };

            const data = await fetchMathApiWithAuth('/api/question/answer', {
                method: 'POST',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify(payload)
            });

            setAnsweredSubmitCount(prev => prev + 1);

            if (data.correct) {
                // 小题答对
                setCorrectCount(prev => prev + 1);
                setCurrentCorrectBlankCount(prev => prev + 1);
            } else {
                // 答错
                if (!HEARTS_IS_INFINITE) {
                    setHeartsRemaining(prev => Math.max(0, prev - 1));
                }
                setCurrentQuestionType(currentQuestion.type);
            }

            // 回传给子组件
            onServerChecked({
                correct: data.correct,
                correctAnswer: data.correctAnswer || '',
                answerExplanation: data.answerExplanation || ''
            });
        } catch (err) {
            console.error(err);
            setError('提交连线答案时发生错误，请稍后重试。');
        }
    };

    // 连线题「全部匹配完成」时 => 防抖处理
    const handleAllMatched = () => {
        if (allMatchedTimer.current) {
            clearTimeout(allMatchedTimer.current);
        }
        // 100ms 防抖
        allMatchedTimer.current = setTimeout(() => {
            setAnsweredQuestionsCount(prev => prev + 1);
            goToNextQuestion(true);
        }, 100);
    };

    const progressPercent = useMemo(() => {
        if (totalQuestions === 0) return 0;
        return Math.round(
            (answeredQuestionsCount / (answeredQuestionsCount + questionsQueue.length)) * 100
        );
    }, [answeredQuestionsCount, questionsQueue.length, totalQuestions]);

    // 若题目队列空 => 显示结果
    useEffect(() => {
        if (!loading && !error && questionsQueue.length === 0) {
            setShowResultModal(true);
        }
    }, [answeredQuestionsCount, totalQuestions, loading, error, questionsQueue.length]);

    // 最终正确率(小题粒度)
    const finalAccuracy = answeredSubmitCount === 0
        ? 0
        : Math.round((correctCount / answeredSubmitCount) * 100);

    // 返回首页
    const handleResultReturn = () => {
        navigate('/');
    };

    // 星星打分(如有需要)
    const getStarRating = (accuracy) => {
        let filledStarsCount = 0;
        if (accuracy === 100) {
            filledStarsCount = 5;
        } else if (accuracy >= 80) {
            filledStarsCount = 4;
        } else if (accuracy >= 60) {
            filledStarsCount = 3;
        } else if (accuracy >= 30) {
            filledStarsCount = 2;
        } else if (accuracy > 0) {
            filledStarsCount = 1;
        }
        const totalStars = 5;
        const filledStars = Array(filledStarsCount).fill('⭐️');
        const emptyStars = Array(totalStars - filledStarsCount).fill('☆');
        return [...filledStars, ...emptyStars];
    };

    // 是否有题
    const hasQuestion = questionsQueue.length > 0;
    // 当前题 or 最后一题
    const questionToDisplay = hasQuestion ? questionsQueue[0] : lastQuestion;

    const [currentQuestionId, setCurrentQuestionId] = useState(null);
    useEffect(() => {
        if (questionToDisplay) {
            setCurrentQuestionId(questionToDisplay.questionId);
        }
    }, [questionToDisplay]);


    // 解析填空题结构 (仅针对 type=2)
    const parseFillInTheBlank = (content) => {
        const regex = /#\{(\d+),(\d+)\}/g;
        const parts = [];
        let lastIndex = 0;
        let match;

        while ((match = regex.exec(content)) !== null) {
            const [placeholder, blankOrder, maxLength] = match;
            const index = match.index;
            if (index > lastIndex) {
                parts.push(content.substring(lastIndex, index));
            }
            parts.push({
                type: 'input',
                blankOrder: Number(blankOrder),
                maxLength: Number(maxLength)
            });
            lastIndex = index + placeholder.length;
        }

        if (lastIndex < content.length) {
            parts.push(content.substring(lastIndex));
        }
        return parts;
    };

    // 当 questionToDisplay 是新题目时，自动跳到第1空
    useEffect(() => {
        if (!questionToDisplay) return;
        const qid = questionToDisplay.questionId;
        if (
            (questionToDisplay.type === QUESTION_TYPES.FILL_IN || questionToDisplay.type === QUESTION_TYPES.VERTICAL_CALC)
            && (HEARTS_IS_INFINITE || heartsRemaining > 0)
            && qid !== previousQuestionIdRef.current
        ) {
            setCurrentBlankOrder(1);
            // 根据第1空 => keyboardIndex
            let kbIdx = 0;
            if (qid && questionToDisplay.keyboards?.length) {
                kbIdx = 0; // 或者根据 your logic
            }
            setCurrentKeyboardIndex(kbIdx);
            clearKeyboardInput();
            setShowKeyboard(true);
            previousQuestionIdRef.current = qid;
        }
    }, [questionToDisplay, heartsRemaining]);

    const handleDecimalFractionChange = (fraction) => {
        setDecimalFraction(fraction);
    };

    const clearKeyboardInput = () => {
        setKeyboardInput('');
        keyboardRef.current?.setInput("");
    }

    if (loading) {
        return <div className="paper-loading">加载中...</div>;
    }
    if (error) {
        return <div className="paper-error">错误: {error}</div>;
    }
    if (paperData && paperData.questions && paperData.questions.length === 0) {
        return <div className="paper-no-data">暂无试卷数据</div>;
    }

    return (
        <div className="paper-page">
            {/* 顶部栏 */}
            <div className="paper-header">
                <button
                    className="paper-back-button"
                    onClick={handleGoBack}
                    aria-label="返回"
                >
                    <svg className="paper-close-icon" viewBox="0 0 24 24" fill="none">
                        <line x1="4" y1="4" x2="20" y2="20" stroke="currentColor"
                              strokeWidth="2" strokeLinecap="round"/>
                        <line x1="20" y1="4" x2="4" y2="20" stroke="currentColor"
                              strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                </button>

                <div className="paper-header-right">
                    <div className="paper-progress-container">
                        <div
                            className="paper-progress-fill"
                            style={{width: `${progressPercent}%`}}
                        />
                    </div>
                    <div className="paper-hearts-container">
                        {HEARTS_IS_INFINITE ? (
                            <span className="paper-heart">❤️∞</span>
                        ) : (
                            heartsRemaining > 0 ? (
                                <span className="paper-heart">❤️ {heartsRemaining}</span>
                            ) : (
                                <span className="paper-heart" style={{color: '#ccc'}}>♡</span>
                            )
                        )}
                    </div>
                </div>
            </div>

            <div className="paper-container">
                {!questionToDisplay && (
                    <div className="paper-no-data">暂无题目可显示</div>
                )}

                {questionToDisplay && (
                    <>
                        {/* (选择题 / 小数格 / 连线 / 竖式 / 自定义图像) => 直接显示 content */}
                        {[QUESTION_TYPES.CHOICE, QUESTION_TYPES.DECIMAL_GRID, QUESTION_TYPES.MATCHING, QUESTION_TYPES.VERTICAL_CALC, QUESTION_TYPES.DIY_IMAGE].includes(questionToDisplay.type) && (
                            <div className="paper-question-title-area">
                                <div className="paper-question-content">
                                    {questionToDisplay.content}
                                </div>
                            </div>
                        )}

                        {/* 填空题 => 解析填空结构 */}
                        {questionToDisplay.type === QUESTION_TYPES.FILL_IN && (
                            <div className="paper-question-title-area">
                                <div className="paper-question-content">
                                    {parseFillInTheBlank(questionToDisplay.content).map((part, idx) => {
                                        if (typeof part === 'string') {
                                            return <span key={idx}>{part}</span>;
                                        } else if (part.type === 'input') {
                                            const blankOrder = part.blankOrder;
                                            const isCurrent = blankOrder === currentBlankOrder;
                                            const value = fillAnswers[blankOrder] || '';
                                            return (
                                                <span key={idx} className="paper-fill-wrapper"
                                                    onClick={() => handleFillClick(blankOrder)}
                                                >
                                                    <span
                                                        className={isCurrent ? "paper-fill-display paper-fill-active" : "paper-fill-display"}
                                                        style={{width: `${part.maxLength}ch`}}
                                                    >
                                                    {value}
                                                    </span>
                                                </span>
                                            );
                                        }
                                        return null;
                                    })}
                                </div>
                            </div>
                        )}

                        {/* 竖式计算图 */}
                        {questionToDisplay.showTime &&
                            questionToDisplay.showTime.type === 'vertical_calculate' && (
                                <div className="paper-question-show-time-area">
                                    <VerticalCalc
                                        dataMatrix={questionToDisplay.showTime.lines || []}
                                        fillAnswers={fillAnswers}
                                        currentBlankOrder={currentBlankOrder}
                                        onPlaceholderClick={handleFillClick}
                                    />
                                </div>
                            )
                        }

                        {/* 连线图 */}
                        {questionToDisplay.showTime &&
                            questionToDisplay.showTime.type === 'link_link' && (
                                <div className="paper-question-show-time-area">
                                    <MatchingGame
                                        leftItems={questionToDisplay.showTime.left}
                                        rightItems={questionToDisplay.showTime.right}
                                        onPairSelect={handleLinkLinkPair}
                                        onAllMatched={handleAllMatched}
                                    />
                                </div>
                            )}

                        {/* 小数格 */}
                        {questionToDisplay.showTime &&
                            questionToDisplay.showTime.type === "decimal_grid" &&
                            questionToDisplay.showTime.params &&
                            questionToDisplay.showTime.params.length >= 3 && (
                                <div className="paper-showtime-grid">
                                    <DecimalGrid
                                        key={`DecimalGrid-${currentQuestionId}`}
                                        gridType={questionToDisplay.showTime.params[0]}
                                        filledCount={Number(questionToDisplay.showTime.params[1])}
                                        gridModify={questionToDisplay.showTime.params[2] === "true"}
                                        onFractionChange={handleDecimalFractionChange}
                                    />
                                </div>
                            )}

                        {/* 自定义图像 */}
                        {questionToDisplay.showTime &&
                            questionToDisplay.showTime.type === "diy_image" && (
                                <div className="paper-question-show-time-area">
                                    <DiyImageShow savedData={questionToDisplay.showTime} showGrid={false}/>
                                </div>
                            )
                        }

                        {/* 错误提示弹窗 */}
                        {wrongAnswerFeedback.show && (
                            <div className="paper-answer-feedback">
                                <div className="paper-answer-feedback-header">
                                    <span className="paper-answer-feedback-icon">❌</span>
                                    <span className="paper-answer-feedback-text">再试试吧！</span>
                                </div>
                                <p className="paper-answer-correct">
                                    正确答案：{wrongAnswerFeedback.correctAnswer}
                                </p>
                                {wrongAnswerFeedback.answerExplanation && (
                                    <p className="paper-answer-explanation">
                                        答案解析：{wrongAnswerFeedback.answerExplanation}
                                    </p>
                                )}
                                <button
                                    className="paper-feedback-confirm-button"
                                    onClick={handleConfirmFeedback}
                                >
                                    我知道了
                                </button>
                            </div>
                        )}

                        {/* 选择题 / 小数格 => 显示 [检查] 按钮 */}
                        {[QUESTION_TYPES.CHOICE, QUESTION_TYPES.DECIMAL_GRID].includes(questionToDisplay.type) && (
                            <>
                                {questionToDisplay.type === QUESTION_TYPES.CHOICE && (
                                    <div className="paper-option-list">
                                        {questionToDisplay.options?.map((option) => {
                                            const isSelected = option.name === selectedOption;
                                            return (
                                                <div
                                                    key={option.name}
                                                    className={`paper-option-button ${isSelected ? 'selected' : ''}`}
                                                    onClick={() => {
                                                        if (!showResultModal && (HEARTS_IS_INFINITE || heartsRemaining > 0)) {
                                                            handleOptionClick(option.name);
                                                        }
                                                    }}
                                                >
                                                    {option.name}. {option.content}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}

                                <button
                                    className="paper-next-button"
                                    onClick={handleSubmitAnswer}
                                    disabled={
                                        (questionToDisplay.type === QUESTION_TYPES.CHOICE && !selectedOption) ||
                                        (questionToDisplay.type === QUESTION_TYPES.DECIMAL_GRID && (decimalFraction === '0.00' || decimalFraction === '0.0')) ||
                                        wrongAnswerFeedback.show ||
                                        showResultModal ||
                                        (heartsRemaining === 0) ||
                                        isChecking
                                    }
                                >
                                    检查
                                </button>
                            </>
                        )}

                        {/* 填空题 / 竖式计算题 => 底部键盘 */}
                        {[QUESTION_TYPES.FILL_IN, QUESTION_TYPES.VERTICAL_CALC].includes(questionToDisplay.type) &&
                            showKeyboard &&
                            (HEARTS_IS_INFINITE || heartsRemaining > 0) &&
                            !wrongAnswerFeedback.show &&
                            !showResultModal &&
                            questionToDisplay.keyboards &&
                            questionToDisplay.keyboards.length > currentKeyboardIndex && (() => {
                                const kbItem = questionToDisplay.keyboards[currentKeyboardIndex];
                                const kbLayout = getKeyboardLayout(kbItem);
                                if (!kbLayout) return null;

                                const buttonTheme = buildButtonThemeFromLayout(kbLayout);
                                return (
                                    <div className="paper-keyboard-container">
                                        <Keyboard
                                            keyboardRef={(r) => (keyboardRef.current = r)}
                                            theme="hg-theme-default paper-keyboard-theme"
                                            layout={{default: kbLayout}}
                                            display={{
                                                '{bksp}': '⌫',
                                                '{done}': '检查'
                                            }}
                                            // 让键盘内的输入与 keyboardInput 绑定
                                            value={keyboardInput}
                                            onChange={handleKeyboardChange}
                                            onKeyPress={handleKeyPress}
                                            buttonTheme={buttonTheme}
                                        />
                                    </div>
                                );
                            })()
                        }
                    </>
                )}
            </div>

            {/* 红心用完弹窗 - 在无限红心模式下不显示 */}
            {!HEARTS_IS_INFINITE && heartsRemaining === 0 && (
                <div className="paper-heartout-overlay">
                    <div className="paper-heartout-modal">
                        <h2>很遗憾，你的红心已经用完了</h2>
                        <p>再试一次吧~</p>
                        <button
                            className="paper-heartout-retry-button"
                            onClick={handleGoBack}
                        >
                            重新再来
                        </button>
                    </div>
                </div>
            )}

            {/* 全部答完 => 结果弹窗 */}
            {showResultModal && (
                <div className="paper-result-overlay">
                    <div className="paper-result-modal">
                        <h2>你真棒！</h2>
                        <p>已完成所有题目</p>
                        <p>本次正确率：{finalAccuracy}%</p>
                        <div className="paper-star-rating">
                            {getStarRating(finalAccuracy).map((star, i) => (
                                <span key={i} className="paper-star">{star}</span>
                            ))}
                        </div>
                        <button
                            className="paper-result-button"
                            onClick={handleResultReturn}
                        >
                            小小学霸，继续挑战！
                        </button>
                    </div>
                </div>
            )}

            {showExitConfirm && (
                <div className="paper-exit-confirm">
                    <div className="paper-answer-feedback">
                        <div className="paper-answer-feedback-header">
                            <span className="paper-answer-feedback-icon">⚠️</span>
                            <span className="paper-answer-feedback-text">书山有路勤为径</span>
                        </div>
                        <p className="paper-answer-correct">确定要退出当前试卷吗？</p>
                        <div style={{display: 'flex', gap: '1rem', justifyContent: 'flex-end', marginTop: '1rem'}}>
                            <button
                                className="paper-feedback-confirm-button"
                                style={{backgroundColor: '#ccc', color: '#000'}}
                                onClick={handleExitCancel}
                            >
                                取消
                            </button>
                            <button
                                className="paper-feedback-confirm-button"
                                onClick={handleExitConfirm}
                            >
                                确认退出
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Paper;
